import { useEffect, useRef, useState, useCallback } from "react";

type NetworkStatus = "checking" | "online" | "offline";

const SUPABASE_URL = "https://zbqpsxyzkblrgqmfepie.supabase.co";
const ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InpicXBzeHl6a2JscmdxbWZlcGllIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NDEzNzI0ODAsImV4cCI6MjA1Njk0ODQ4MH0.vMWGE3M3PHIfPa59D1KrErdTtXFSdRvp-OfiWbWS59k";

const PING_TIMEOUT_MS = 4000;
const RECOVERY_INTERVAL_MS = 5000;
const REQUIRED_FAILURES = 2;

export function useNetworkStatus() {
  const [status, setStatus] = useState<NetworkStatus>(
    typeof navigator !== "undefined" && navigator.onLine ? "online" : "checking"
  );

  const isMountedRef = useRef(true);
  const failureCountRef = useRef(0);
  const recoveryIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const ping = useCallback(async (): Promise<boolean> => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
      await fetch(`${SUPABASE_URL}/auth/v1/health`, {
        method: "GET",
        signal: controller.signal,
        cache: "no-store",
        headers: { apikey: ANON_KEY, Authorization: `Bearer ${ANON_KEY}` },
      });
      clearTimeout(timeoutId);
      return true;
    } catch {
      return false;
    }
  }, []);

  const stopRecoveryPolling = useCallback(() => {
    if (recoveryIntervalRef.current) {
      clearInterval(recoveryIntervalRef.current);
      recoveryIntervalRef.current = null;
    }
  }, []);

  const startRecoveryPolling = useCallback(() => {
    stopRecoveryPolling();
    recoveryIntervalRef.current = setInterval(async () => {
      if (!isMountedRef.current) return;
      const isOnline = await ping();
      if (isOnline && isMountedRef.current) {
        failureCountRef.current = 0;
        setStatus("online");
        stopRecoveryPolling();
      }
    }, RECOVERY_INTERVAL_MS);
  }, [ping, stopRecoveryPolling]);

  useEffect(() => {
    isMountedRef.current = true;

    const initialCheck = async () => {
      const isOnline = await ping();
      if (!isMountedRef.current) return;
      if (isOnline) {
        setStatus("online");
      } else if (!navigator.onLine) {
        setStatus("offline");
        startRecoveryPolling();
      }
    };
    const timer = setTimeout(initialCheck, 500);

    const handleOnline = () => {
      failureCountRef.current = 0;
      setStatus("online");
      stopRecoveryPolling();
    };

    const handleOffline = async () => {
      failureCountRef.current++;
      const isOnline = await ping();
      if (isOnline) {
        failureCountRef.current = 0;
        return;
      }
      failureCountRef.current++;
      if (failureCountRef.current >= REQUIRED_FAILURES) {
        setStatus("offline");
        startRecoveryPolling();
      }
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      isMountedRef.current = false;
      clearTimeout(timer);
      stopRecoveryPolling();
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [ping, startRecoveryPolling, stopRecoveryPolling]);

  return { status, isOnline: status !== "offline" };
}
